import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { complianceApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Search,
  UserPlus,
  MoreHorizontal,
  ChevronUp,
  ChevronDown,
  Eye,
  Upload,
  Send,
  ClipboardList,
  Download,
  Archive,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  CircleDot,
  ChevronsUpDown,
  ShieldCheck,
  ShieldAlert,
  FileWarning,
} from 'lucide-react';
import { toast } from 'sonner';

const SAVED_VIEWS = [
  { key: 'all', label: 'All Drivers' },
  { key: 'action_required', label: 'Action Required' },
  { key: 'non_compliant', label: 'Non-Compliant' },
  { key: 'expiring_cdl', label: 'Expiring CDL' },
  { key: 'expiring_medical', label: 'Expiring Medical' },
  { key: 'missing_docs', label: 'Missing Docs' },
  { key: 'recently_added', label: 'Recently Added' },
];

function StatusBadge({ status }) {
  const map = {
    active: { label: 'Active', cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
    pending: { label: 'Pending', cls: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400', icon: Clock },
    non_compliant: { label: 'Non-Compliant', cls: 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-400', icon: XCircle },
    disqualified: { label: 'Disqualified', cls: 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-400', icon: XCircle },
    archived: { label: 'Archived', cls: 'bg-muted border-border text-muted-foreground', icon: CircleDot },
    not_hired: { label: 'Not Hired', cls: 'bg-muted border-border text-muted-foreground', icon: CircleDot },
  };
  const conf = map[status] || map.active;
  const Icon = conf.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium border ${conf.cls}`} data-testid={`status-badge-${status}`}>
      <Icon className="h-3 w-3" />{conf.label}
    </span>
  );
}

function CompliancePill({ score }) {
  let cls = 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400';
  let icon = ShieldCheck;
  if (score < 60) {
    cls = 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-400';
    icon = ShieldAlert;
  } else if (score < 80) {
    cls = 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400';
    icon = AlertTriangle;
  }
  const Icon = icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-bold border ${cls}`}>
      <Icon className="h-3 w-3" />{score}%
    </span>
  );
}

function ExpirationCell({ date }) {
  if (!date) return <span className="text-xs text-muted-foreground">Not set</span>;
  const d = date.slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const expDate = new Date(d);
  const todayDate = new Date(today);
  const diff = Math.ceil((expDate - todayDate) / (1000 * 60 * 60 * 24));

  let cls = 'text-foreground';
  let warning = null;
  if (diff < 0) {
    cls = 'text-rose-600 dark:text-rose-400 font-semibold';
    warning = <XCircle className="h-3 w-3 text-rose-500 inline ml-1" />;
  } else if (diff <= 30) {
    cls = 'text-amber-600 dark:text-amber-400 font-medium';
    warning = <AlertTriangle className="h-3 w-3 text-amber-500 inline ml-1" />;
  }

  return (
    <span className={`text-xs ${cls}`}>
      {new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      {warning}
    </span>
  );
}

export default function DriverManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [drivers, setDrivers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeView, setActiveView] = useState(searchParams.get('view') || 'all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  const loadDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        search: search || undefined,
        view: activeView !== 'all' ? activeView : undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
        page,
        page_size: 50,
      };
      const res = await complianceApi.getDrivers(params);
      setDrivers(res.data.drivers || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  }, [search, activeView, sortBy, sortDir, page]);

  useEffect(() => { loadDrivers(); }, [loadDrivers]);

  useEffect(() => {
    const v = searchParams.get('view');
    if (v && v !== activeView) setActiveView(v);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const handleViewChange = (view) => {
    setActiveView(view);
    setPage(1);
    setSearchParams(view !== 'all' ? { view } : {});
  };

  const SortHeader = ({ col, children, className = '' }) => {
    const active = sortBy === col;
    return (
      <TableHead
        className={`cursor-pointer select-none whitespace-nowrap hover:bg-accent/50 transition-colors duration-150 ${className}`}
        onClick={() => handleSort(col)}
        data-testid={`sort-${col}`}
      >
        <div className="flex items-center gap-1">
          {children}
          {active ? (sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ChevronsUpDown className="h-3 w-3 text-muted-foreground/40" />}
        </div>
      </TableHead>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-cabinet text-3xl font-bold tracking-tight text-foreground" data-testid="driver-mgmt-title">Driver Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{total} driver{total !== 1 ? 's' : ''} total</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/drivers">
            <Button variant="outline" size="sm" data-testid="add-driver-btn">
              <UserPlus className="h-4 w-4 mr-1.5" /> Add Driver
            </Button>
          </Link>
          <a href="/apply" target="_blank" rel="noopener noreferrer">
            <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="invite-btn">
              <Send className="h-4 w-4 mr-1.5" /> Invite Applicant
            </Button>
          </a>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search drivers by name, email, phone, license..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-10 h-9 text-sm"
            data-testid="driver-search-input"
          />
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-1" data-testid="saved-views">
        {SAVED_VIEWS.map((v) => (
          <button
            key={v.key}
            onClick={() => handleViewChange(v.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors duration-150 border ${
              activeView === v.key
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card text-muted-foreground border-border hover:bg-accent'
            }`}
            data-testid={`view-${v.key}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="driver-table-container">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border">
                <SortHeader col="name">Driver</SortHeader>
                <TableHead className="whitespace-nowrap">Status</TableHead>
                <SortHeader col="compliance_score">Compliance</SortHeader>
                <SortHeader col="license_expiration" className="hidden md:table-cell">CDL Expiration</SortHeader>
                <SortHeader col="medical_card_expiration" className="hidden md:table-cell">Medical Card</SortHeader>
                <SortHeader col="missing_items" className="hidden lg:table-cell">Missing Items</SortHeader>
                <TableHead className="hidden lg:table-cell whitespace-nowrap">Files</TableHead>
                <TableHead className="w-10">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }).map((_, j) => (
                      <TableCell key={j} className={j >= 3 && j <= 4 ? 'hidden md:table-cell' : j >= 5 && j <= 6 ? 'hidden lg:table-cell' : ''}>
                        <Skeleton className="h-5 w-full max-w-[120px]" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : drivers.length > 0 ? (
                drivers.map((driver) => (
                  <TableRow key={driver.id} className="hover:bg-accent/30 transition-colors duration-150" data-testid={`driver-row-${driver.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center text-xs font-bold text-muted-foreground flex-shrink-0">
                          {driver.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{driver.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{driver.email || driver.phone || '—'}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><StatusBadge status={driver.computed_status || driver.status} /></TableCell>
                    <TableCell><CompliancePill score={driver.compliance_score} /></TableCell>
                    <TableCell className="hidden md:table-cell"><ExpirationCell date={driver.license_expiration} /></TableCell>
                    <TableCell className="hidden md:table-cell"><ExpirationCell date={driver.medical_card_expiration} /></TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {driver.missing_items_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 font-medium">
                          <FileWarning className="h-3 w-3" />{driver.missing_items_count}
                        </span>
                      ) : (
                        <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" /> Complete
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-xs text-muted-foreground">{driver.file_count}</span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`actions-${driver.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem asChild>
                            <Link to="/drivers" className="flex items-center gap-2 cursor-pointer"><Eye className="h-4 w-4" /> View Profile</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to="/driver-files" className="flex items-center gap-2 cursor-pointer"><Upload className="h-4 w-4" /> Upload Document</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem className="flex items-center gap-2 cursor-pointer"><ClipboardList className="h-4 w-4" /> Start Review</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="flex items-center gap-2 cursor-pointer"><Download className="h-4 w-4" /> Export File</DropdownMenuItem>
                          <DropdownMenuItem className="flex items-center gap-2 cursor-pointer text-muted-foreground"><Archive className="h-4 w-4" /> Archive</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <ShieldCheck className="h-10 w-10 text-muted-foreground/20" />
                      <p className="text-sm text-muted-foreground">
                        {search ? 'No drivers match your search' : activeView !== 'all' ? 'No drivers in this view' : 'No drivers added yet'}
                      </p>
                      {!search && activeView === 'all' && (
                        <Link to="/drivers">
                          <Button size="sm" variant="outline" className="mt-2"><UserPlus className="h-4 w-4 mr-1.5" /> Add First Driver</Button>
                        </Link>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {total > 50 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="text-xs text-muted-foreground">Showing {((page - 1) * 50) + 1}–{Math.min(page * 50, total)} of {total}</p>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <Button variant="outline" size="sm" disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
